import { useEffect, useRef, useState } from "react";

interface CodeInputProps {
  length: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function CodeInput({
  length,
  value,
  onChange,
  onComplete,
  autoFocus,
  className,
  "data-testid": testId,
}: CodeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [caret, setCaret] = useState(0);
  // Touch only: the overlay input gives iOS nothing to anchor a Paste bubble to,
  // so there is no way in by hand (BDAY-235). Desktop keeps Cmd/Ctrl+V and its
  // own context menu, and stays pixel-identical to legacy.
  const [canPaste, setCanPaste] = useState(false);
  useEffect(() => {
    setCanPaste(
      typeof navigator !== "undefined" &&
        !!navigator.clipboard?.readText &&
        window.matchMedia?.("(pointer: coarse)").matches,
    );
  }, []);

  const activeIndex = focused ? Math.min(caret, length - 1) : -1;

  const placeCaret = (pos: number, nextVal: string) => {
    const input = inputRef.current;
    if (!input) return;
    const clamped = Math.max(0, Math.min(pos, length));
    requestAnimationFrame(() => {
      if (clamped < nextVal.length) {
        input.setSelectionRange(clamped, clamped + 1);
      } else {
        input.setSelectionRange(nextVal.length, nextVal.length);
      }
    });
    setCaret(clamped);
  };

  // BDAY-242: `onComplete` auto-submits, and the server spends one of three
  // verification attempts per submission. Correcting a digit never shortens the
  // value (`placeCaret` selects the character under the caret, so a keystroke
  // replaces in place), so firing on every complete value burned an attempt per
  // keystroke. Fire only on the transition INTO a complete code; a correction is
  // submitted by the explicit Verify button every consumer renders. Whole-code
  // input (paste, autofill) is deliberate and always counts. Never the same code
  // twice until the field drops below full (including the consumer clearing it).
  const lastFired = useRef<string | null>(null);
  useEffect(() => {
    if (value.length < length) lastFired.current = null;
  }, [value, length]);

  const commit = (nextVal: string, nextPos: number, deliberate = false) => {
    const wasComplete = value.length === length;
    onChange(nextVal);
    placeCaret(nextPos, nextVal);
    if (!onComplete || nextVal.length !== length) return;
    if (wasComplete && !deliberate) return; // mid-correction, not a submission
    if (lastFired.current === nextVal) return;
    lastFired.current = nextVal;
    onComplete(nextVal);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    const input = inputRef.current;
    if (!container || !input) return;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = Math.min(length - 1, Math.max(0, Math.floor(x / (rect.width / length))));
    input.focus();
    placeCaret(Math.min(idx, value.length), value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const input = inputRef.current;
    const selStart = input?.selectionStart ?? caret;
    const selEnd = input?.selectionEnd ?? caret;
    const hasRange = selStart !== selEnd;
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      if (selStart >= length) return;
      const tail = Math.max(selEnd, selStart + 1);
      const nextVal = (value.slice(0, selStart) + e.key + value.slice(tail)).slice(0, length);
      commit(nextVal, selStart + 1);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      if (hasRange) {
        commit(value.slice(0, selStart) + value.slice(selEnd), selStart);
      } else if (selStart > 0) {
        const pos = selStart - 1;
        commit(value.slice(0, pos) + value.slice(pos + 1), pos);
      }
    } else if (e.key === "Delete") {
      e.preventDefault();
      if (hasRange) {
        commit(value.slice(0, selStart) + value.slice(selEnd), selStart);
      } else if (selStart < value.length) {
        commit(value.slice(0, selStart) + value.slice(selStart + 1), selStart);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      placeCaret(Math.max(0, caret - 1), value);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      placeCaret(Math.min(value.length, caret + 1), value);
    } else if (e.key === "Home") {
      e.preventDefault();
      placeCaret(0, value);
    } else if (e.key === "End") {
      e.preventDefault();
      placeCaret(value.length, value);
    } else if (e.key.length === 1) {
      // Any other printable key would replace the selected digit with a character
      // `handleChange` then strips — a silent deletion. (IME/composition keys report
      // "Unidentified"/"Process" and fall through to `handleChange` on purpose.)
      e.preventDefault();
    }
  };

  /** Autofill, dictation and predictive keyboards set the value outright rather
   *  than going through onKeyDown/onPaste; this used to be a no-op, so they were
   *  silently dropped (BDAY-235). */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    const digits = raw.slice(0, length);
    if (digits === value) return;
    // Align the edit against the old value (shared prefix/suffix, on the unsliced
    // digits so a mid-string insert doesn't read as a shifted rewrite). A one- or
    // two-digit edit is a correction; three or more new digits in one event is a code
    // arriving (autofill over a full wrong code) and submits even when already full.
    let pre = 0;
    while (pre < raw.length && pre < value.length && raw[pre] === value[pre]) pre++;
    let suf = 0;
    while (
      suf < raw.length - pre &&
      suf < value.length - pre &&
      raw[raw.length - 1 - suf] === value[value.length - 1 - suf]
    ) {
      suf++;
    }
    const whole = raw.length - pre - suf >= 3;
    const pos = Math.min(e.target.selectionStart ?? digits.length, digits.length);
    commit(digits, whole ? digits.length : pos, whole);
  };

  const readClipboard = async () => {
    // Denied or unsupported reads as empty — the field still takes a normal paste.
    const text = String((await navigator.clipboard.readText().catch(() => "")) ?? "");
    const digits = text.replace(/\D/g, "").slice(0, length);
    if (digits) commit(digits, Math.min(digits.length, length), true);
    inputRef.current?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Synthetic paste events (extensions, password managers) can arrive with a null
    // clipboardData; let those through untouched so `handleChange` takes whatever they
    // insert, as it does for autofill (Sentry BDAY-WEB-R, /forgot, 2026-10-22).
    if (!e.clipboardData) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    const pos = Math.min(caret, length);
    const nextVal = (value.slice(0, pos) + pasted + value.slice(pos + pasted.length)).slice(
      0,
      length,
    );
    commit(nextVal, Math.min(pos + pasted.length, length), true);
  };

  return (
    // The wrapper takes the box the cell row used to own, so nothing moves on
    // the pages that render this (the Paste button only appears on touch).
    <div className={`flex w-full max-w-sm flex-col gap-3 ${className ?? ""}`}>
      {/* Cells have an intrinsic size: `flex-1` alone collapsed to slivers inside a
        `flex-col items-center` parent (the forgot-password page, 2026-09-23). */}
      <div
        ref={containerRef}
        className="relative flex w-full justify-center gap-2"
        onClick={handleClick}
      >
        {Array.from({ length }).map((_, i) => (
          <div
            key={i}
            className={`pointer-events-none h-12 w-10 shrink-0 sm:h-14 sm:w-11 flex items-center justify-center rounded-lg border bg-background text-xl font-semibold transition-colors ${
              i === activeIndex ? "border-primary border-2" : "border-input"
            }`}
          >
            {value[i] ?? ""}
          </div>
        ))}
        <input
          ref={inputRef}
          data-testid={testId}
          type="text"
          inputMode="numeric"
          // Lets iOS Safari and Chrome on Android offer a texted/emailed code above
          // the keyboard (BDAY-235).
          autoComplete="one-time-code"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          className="absolute inset-0 z-10 opacity-0 cursor-default"
          autoFocus={autoFocus}
        />
      </div>
      {canPaste && (
        <button
          type="button"
          onClick={() => void readClipboard()}
          className="self-center text-sm font-semibold text-primary underline"
        >
          Paste code
        </button>
      )}
    </div>
  );
}
